import pillTemplate from 'filter/templates/pill'

import { ENTER_KEYCODE, ESCAPE_KEYCODE, LEFT_KEYCODE, RIGHT_KEYCODE, DELETE_KEYCODE, BACKSPACE_KEYCODE } from 'util/keycodes'

import { caretAtStart, hasOverflowLeft, hasOverflowRight, scrollIntoView } from './scroll'

export default class SearchInput {
  constructor ($el, selectorPrefix) {
    this.selectedIndex = null
    this.selectorPrefix = selectorPrefix
    this.$el = $el
    this.$wrapper = this.$el.find(`.${selectorPrefix}__wrapper`)
    this.$inner = this.$el.find(`.${selectorPrefix}__inner`)
    this.$input = this.$el.find(`.${selectorPrefix}__input`)
    this.$hidden = this.$el.find(`.${selectorPrefix}__hidden`)
    this.$tags = this.$el.find(`.${selectorPrefix}__pills`)

    this.overflowLeftClass = this.$wrapper.data('overflow-left')
    this.overflowRightClass = this.$wrapper.data('overflow-right')
    this.selectedPillClass = this.$wrapper.data('selected-pill')

    this.tags = new Set()
    this.addEventHandlers()
  }

  addEventHandlers () {
    this.$el.on('submit', this.onSubmit.bind(this))

    this.$inner.on('scroll', this.onScroll.bind(this))

    this.$input.on('input', this.onInput.bind(this))
    this.$input.on('keydown', this.onKeydown.bind(this))
    this.$input.on('click', this.unselect.bind(this))

    this.$tags.on('click', `.${this.selectorPrefix}__pill`, this.onTagClick.bind(this))
    this.$tags.on('click', `.${this.selectorPrefix}__remove`, this.onTagRemove.bind(this))

    EventBus.on('ontology_light_typeahead:select', this.onOntologyLightTypeaheadSelect.bind(this))
  }

  unselect () {
    this.selectedIndex = null
    this.renderSelected()
  }

  onInput (event) {
    const val = this.$input.val()
    // If there's no comma, we don't need to do anything
    if (val.indexOf(',') === -1 && val.indexOf(' ') === -1) {
      return
    }

    const tags = val.split(/[\s,]/).map(tag => tag.trim()).filter(tag => tag !== '')

    // If there's input after the last comma, we keep that last bit in the input
    if (val.slice(-1) !== ',' && val.slice(-1) !== ' ') {
      const last = tags.pop()
      this.$input.val(last)
    } else {
      this.resetInput()
    }
    tags.forEach(tag => this.tags.add(tag))
    this.renderTags()
  }

  onKeydown (event) {
    const atStart = caretAtStart(this.$input.get(0))
    const selected = this.selectedIndex !== null
    const val = this.$input.val()
    // Adding a new tag from input
    if (val === '' && event.keyCode === ENTER_KEYCODE) {
      // Submit form
    } else if (event.keyCode === ENTER_KEYCODE) {
      event.preventDefault()
      this.addCurrentInput()
      scrollIntoView(this.$input.get(0), this.$inner.get(0))
    // Resetting input when input is focussed
    } else if (event.keyCode === ESCAPE_KEYCODE && !selected) {
      this.resetInput()
    // Delete selected tag
    } else if (selected && (event.keyCode === BACKSPACE_KEYCODE || event.keyCode === DELETE_KEYCODE)) {
      event.preventDefault()
      this.removeSelected()
    // Select left from caret or last selected
    } else if ((event.keyCode === LEFT_KEYCODE || event.keyCode === BACKSPACE_KEYCODE) && (selected || atStart)) {
      event.preventDefault()
      this.selectLeft()
    // Select right from selected (if any)
    } else if (event.keyCode === RIGHT_KEYCODE && selected) {
      event.preventDefault()
      this.selectRight()
    // Otherwise normal text input
    } else {
      this.selectedIndex = null
      scrollIntoView(this.$input.get(0), this.$inner.get(0))
      this.renderSelected()
    }
  }

  onOntologyLightTypeaheadSelect () {
    this.addCurrentInput()
    scrollIntoView(this.$input.get(0), this.$inner.get(0))
  }

  onScroll (event) {
    this.$wrapper.toggleClass(this.overflowLeftClass, hasOverflowLeft(this.$inner))
    this.$wrapper.toggleClass(this.overflowRightClass, hasOverflowRight(this.$inner))
  }

  onSubmit (event) {
    this.addCurrentInput()
    this.renderTags()
  }

  onTagClick (event) {
    this.selectedIndex = $(event.currentTarget).index()
    this.$input.focus()
    this.renderSelected()
  }

  onTagRemove (event) {
    $(event.currentTarget).parent().click()
    this.removeSelected()
    this.selectedIndex = null
    this.renderSelected()
    return false
  }

  addCurrentInput () {
    const value = this.$input.val().trim()
    if (value === '') {
      return
    }
    this.tags.add(value)
    this.resetInput()
    this.renderTags()
  }

  resetInput () {
    this.$input.val('')
  }

  selectLeft () {
    // No tags left
    if (this.tags.size === 0) {
      this.selectedIndex = null
    // No tag select => select closest to caret
    } else if (this.selectedIndex === null) {
      this.selectedIndex = this.tags.size - 1
    // Select next tag to the left
    } else {
      this.selectedIndex = Math.max(0, this.selectedIndex - 1)
    }
    this.renderSelected()
  }

  selectRight () {
    // Last tag is currently selected, so unselect
    if (this.selectedIndex === this.tags.size - 1) {
      this.selectedIndex = null
    // Otherwise select tag to the right
    } else {
      this.selectedIndex = Math.min(this.tags.size - 1, this.selectedIndex + 1)
    }
    this.renderSelected()
  }

  removeSelected () {
    this.tags.delete([...this.tags][this.selectedIndex])
    this.renderTags()
    this.selectLeft()
    this.renderSelected()
  }

  renderSelected () {
    this.$tags.children().removeClass(this.selectedPillClass)

    if (this.selectedIndex !== null) {
      const el = this.$tags.children().eq(this.selectedIndex).addClass(this.selectedPillClass).get(0)
      scrollIntoView(el, this.$inner.get(0))
    }
  }

  renderTag (tag) {
    return pillTemplate({ key: tag, label: tag }, 'search-input__pill js-search-input__pill')
  }

  renderTags () {
    this.$tags.html([...this.tags].map(this.renderTag).join(''))
    this.$hidden.val([...this.tags].join(','))
  }
}

$(() => {
  const selector = 'js-search-input'
  $(`.${selector}`).map(function () {
    new SearchInput($(this), selector)
  })
})
